import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Wordmark } from "@/components/Brand";
import { Splash } from "@/components/Protected";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ dev_mode: true }));
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  if (loading) return <Splash />;

  const devLogin = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/dev-login", { name: name.trim(), email: email.trim() });
      await refresh();
      navigate("/", { replace: true });
    } catch {
      toast.error("Could not sign in. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const shopifyLogin = () => {
    window.location.href = `${API}/auth/login`;
  };

  return (
    <div className="min-h-screen bg-[#D8C3A5] flex flex-col items-center justify-center px-6 py-10 text-[#1F2A44]" data-testid="login-page">
      <Logo size={96} />
      <h1 className="mt-6 text-4xl"><Wordmark className="text-[#1F2A44]" /></h1>
      <p className="mt-2 text-sm text-[#1F2A44]/70">Powered by Wonderbean Coffee Co.</p>
      <p className="mt-1 text-base text-[#1F2A44]/90">Better coffee, made simple.</p>

      <div className="w-full max-w-sm mt-10">
        {config && !config.dev_mode ? (
          <Button
            onClick={shopifyLogin}
            data-testid="shopify-login-btn"
            className="w-full h-14 rounded-full bg-[#F57C00] text-[#1F2A44] text-base font-semibold hover:bg-[#E86F00] active:scale-95 transition-all"
          >
            Lets brew!!
          </Button>
        ) : (
          <form onSubmit={devLogin} className="space-y-4">
            <div className="rounded-2xl border border-[#D8C3A5]/25 bg-[#2E5E3E] p-3 text-sm leading-relaxed text-[#EDE0CC]/90">
              Preview sign-in for approved testers. Wonderbean customer login will be connected before launch.
            </div>
            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="dev-name-input"
              className="h-12 rounded-xl bg-[#2E5E3E] border-[#EDE0CC]/40 text-[#EDE0CC] placeholder:text-[#EDE0CC]/60"
            />
            <Input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="dev-email-input"
              className="h-12 rounded-xl bg-[#2E5E3E] border-[#EDE0CC]/40 text-[#EDE0CC] placeholder:text-[#EDE0CC]/60"
            />
            <Button
              type="submit"
              disabled={busy}
              data-testid="dev-login-btn"
              className="w-full h-14 rounded-full bg-[#F57C00] text-[#1F2A44] text-base font-semibold hover:bg-[#E86F00] active:scale-95 transition-all disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Continue"}
            </Button>
          </form>
        )}
      </div>
      <p className="mt-10 text-xs text-[#1F2A44]/65">Sign in to start brewing.</p>
    </div>
  );
}
